import React, { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Button,
  Switch,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BeneficiariesState } from 'stores/beneficiaries';
import BeneficiariesService, { CreateBeneficiaryDTO, PixKeyType, BankAccountType } from 'services/beneficiaries.service';
import { cleanPixKey, getPixKeyType, isValidPixKey } from 'utils/pix';
import { useToasts } from 'hooks/toasts';
import { AxiosResponse } from 'axios';
import BeneficiaryFields from './BeneficiaryFields';
import AddressFields from './AddressFields';
import LatePaymentFields from './LatePaymentFields';
import EarlyDiscountFields from './EarlyDiscountFields';
import AccountOrPixFields from './AccountOrPixFields';
import DeleteBeneficiaryDialog from './DeleteBeneficiaryDialog';

export enum EBeneficiaryType {
  ACCOUNT = 'conta',
  PIX_KEY = 'pix',
}

type CounterpartyModalProps = {
  open: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  initialData?: any;
  onSubmitted?: () => void;
};

const CounterpartyModal: FC<CounterpartyModalProps> = ({ open, onClose, mode = 'create', initialData, onSubmitted }) => {
  const mapEarlyDiscountTypeDisplay = (t: ('FIXED' | 'PERCENTAGE') | null | undefined): string => {
    if (!t) return '';
    return t === 'FIXED' ? 'fixo' : 'percentual';
  };

  enum EAccountTypePortuguese {
    CORRENTE = 'CORRENTE',
    POUPANCA = 'POUPANCA',
    PAGAMENTO = 'PAGAMENTO',
  }

  const ACCOUNT_TYPE_MAP: Record<EAccountTypePortuguese, BankAccountType> = {
    [EAccountTypePortuguese.CORRENTE]: 'CHECKING',
    [EAccountTypePortuguese.POUPANCA]: 'SAVINGS',
    [EAccountTypePortuguese.PAGAMENTO]: 'PAYMENT',
  };

  const mapServiceToPortuguese = (t?: string | BankAccountType | null): EAccountTypePortuguese | '' => {
    switch (t) {
      case 'CHECKING':
        return EAccountTypePortuguese.CORRENTE;
      case 'SAVINGS':
        return EAccountTypePortuguese.POUPANCA;
      case 'PAYMENT':
        return EAccountTypePortuguese.PAGAMENTO;
      default:
        return '';
    }
  };

  const [selectedType, setSelectedType] = React.useState<EBeneficiaryType>(EBeneficiaryType.ACCOUNT);
  const { type } = useSelector<IRootState, BeneficiariesState>(({ beneficiaries }) => beneficiaries);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [allowLatePayment, setAllowLatePayment] = React.useState(false);
  const [allowEarlyDiscount, setAllowEarlyDiscount] = React.useState(false);
  const toast = useToasts();
  const service = React.useMemo(() => new BeneficiariesService(), []);

  const isEnvio = type !== 'boleto';

  const validationSchema = React.useMemo(() => {
    const base = {
      companyName: Yup.string().required('Campo obrigatório'),
      documentNumber: Yup.string().required('Campo obrigatório'),
      tradeName: Yup.string().required('Campo obrigatório'),
    };

    if (isEnvio) {
      if (selectedType === EBeneficiaryType.PIX_KEY) {
        return Yup.object({
          ...base,
          pixKey: Yup.string()
            .required('Chave PIX obrigatória')
            .test('valid-pix-format', 'Formato de chave PIX inválido', (value) => {
              return isValidPixKey(value || '');
            }),
        });
      }
      return Yup.object({
        ...base,
        bankCode: Yup.string().required('Instituição financeira obrigatória'),
        bankBranch: Yup.string().required('Agência obrigatória'),
        bankAccount: Yup.string().required('Conta obrigatória'),
        accountTypePt: Yup.string().required('Tipo de conta obrigatório'),
      });
    }

    let shape: Record<string, any> = {
      ...base,
      postalCode: Yup.string().required('CEP obrigatório'),
      street: Yup.string().required('Logradouro obrigatório'),
      streetNumber: Yup.string().required('Número obrigatório'),
      neighborhood: Yup.string().required('Bairro obrigatório'),
      city: Yup.string().required('Cidade obrigatória'),
      stateUf: Yup.string().required('Estado obrigatório'),
    };

    if (allowLatePayment) {
      shape = {
        ...shape,
        lateFinePercent: Yup.string().typeError('Informe a multa').required('Multa obrigatória'),
        lateMonthlyInterestPercent: Yup.string().typeError('Informe os juros').required('Juros obrigatórios'),
        lateDays: Yup.number().typeError('Informe os dias').required('Quantidade de dias obrigatória'),
      };
    }

    if (allowEarlyDiscount) {
      shape = {
        ...shape,
        earlyDiscountType: Yup.string().required('Tipo de desconto obrigatório'),
        earlyDiscountValue: Yup.string().typeError('Informe o valor').required('Valor obrigatório'),
        earlyDiscountDays: Yup.number().typeError('Informe os dias').required('Quantidade de dias obrigatória'),
      };
    }

    return Yup.object(shape);
  }, [isEnvio, selectedType, allowLatePayment, allowEarlyDiscount]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      companyName: initialData?.businessName || '',
      tradeName: initialData?.tradingName || '',
      documentNumber: initialData?.taxNumber || '',
      pixKey: initialData?.pixKey || '',
      bankCode: initialData?.bankCode || '',
      bankBranch: initialData?.branch || '',
      bankAccount:
        initialData?.accountNumber && initialData?.accountDigit ? `${initialData.accountNumber}-${initialData.accountDigit}` : '',
      accountTypePt: mapServiceToPortuguese(initialData?.accountType) || '',
      postalCode: initialData?.postalCode || '',
      street: initialData?.street || '',
      streetNumber: initialData?.streetNumber || '',
      addressComplement: initialData?.addressComplement || '',
      neighborhood: initialData?.neighborhood || '',
      city: initialData?.city || '',
      stateUf: initialData?.state || '',
      lateFinePercent: initialData?.lateFeePercentage ? String(initialData.lateFeePercentage) : '',
      lateMonthlyInterestPercent: initialData?.latePaymentInterestRate ? String(initialData.latePaymentInterestRate) : '',
      lateDays: initialData?.latePaymentGraceDays ? String(initialData.latePaymentGraceDays) : '',
      earlyDiscountType: mapEarlyDiscountTypeDisplay(initialData?.earlyPaymentConditionType),
      earlyDiscountValue: initialData?.earlyDiscountValue ? String(initialData.earlyDiscountValue) : '',
      earlyDiscountDays: initialData?.earlyPaymentDiscountDays ? String(initialData.earlyPaymentDiscountDays) : '',
      category: initialData?.category || '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const unmaskedDocument = (values.documentNumber || '').replace(/\D/g, '');
        const beneficiaryType: CreateBeneficiaryDTO['beneficiaryType'] = unmaskedDocument.length <= 11 ? 'PERSON' : 'COMPANY';
        const payload: CreateBeneficiaryDTO = {
          businessName: values.companyName || undefined,
          tradingName: values.tradeName || undefined,
          beneficiaryType,
          counterpartyType: isEnvio ? 'FUNDS_TRANSFER' : 'BOLETO_ISSUANCE',
          taxNumber: unmaskedDocument || undefined,
          pixName: values.companyName || undefined,
          pixKey: undefined,
          pixKeyType: undefined,
          bankCode: undefined,
          bankBranch: undefined,
          bankAccount: undefined,
          bankAccountDigit: undefined,
          bankAccountType: undefined,
          postalCode: undefined,
          street: undefined,
          streetNumber: undefined,
          addressComplement: undefined,
          neighborhood: undefined,
          city: undefined,
          state: undefined,
          allowLatePayment: undefined,
          lateFeePercentage: undefined,
          latePaymentInterestRate: undefined,
          latePaymentGraceDays: undefined,
          earlyPaymentDiscountEnabled: undefined,
          earlyPaymentConditionType: undefined,
          earlyDiscountValue: undefined,
          earlyPaymentDiscountDays: undefined,
          category: values.category,
        };

        if (isEnvio) {
          if (selectedType === 'pix') {
            payload.pixKey = values.pixKey ? cleanPixKey(values.pixKey) : undefined;
            try {
              const epix = getPixKeyType(values.pixKey || '');
              const mapped: PixKeyType = epix === 'RANDOM' ? 'EVP' : (epix as PixKeyType);
              payload.pixKeyType = mapped;
            } catch (e) {
              toast.triggerToast('Chave PIX inválida. Verifique o formato.', { variant: 'error' });
              setSubmitting(false);
              return;
            }
          } else {
            payload.bankCode = values.bankCode || undefined;
            payload.bankBranch = values.bankBranch || undefined;
            const accRaw = (values.bankAccount || '').replace(/\D/g, '');
            const accDigit = accRaw ? accRaw.slice(-1) : '';
            const accNumber = accRaw ? accRaw.slice(0, -1) : '';
            payload.bankAccount = accNumber || undefined;
            payload.bankAccountDigit = accDigit || undefined;
            const mappedType: BankAccountType | undefined = values.accountTypePt
              ? ACCOUNT_TYPE_MAP[values.accountTypePt as EAccountTypePortuguese]
              : undefined;
            payload.bankAccountType = mappedType || undefined;
          }
        } else {
          payload.postalCode = values.postalCode.replace(/\D/g, '') || undefined;
          payload.street = values.street || undefined;
          payload.streetNumber = values.streetNumber || undefined;
          payload.addressComplement = values.addressComplement || undefined;
          payload.neighborhood = values.neighborhood || undefined;
          payload.city = values.city || undefined;
          payload.state = values.stateUf || undefined;
          payload.category = values.category || undefined;

          payload.allowLatePayment = allowLatePayment;
          payload.lateFeePercentage = allowLatePayment ? Number(values.lateFinePercent.replace(/\D/g, '')) : undefined;
          payload.latePaymentInterestRate = allowLatePayment ? Number(values.lateMonthlyInterestPercent.replace(/\D/g, '')) : undefined;
          payload.latePaymentGraceDays = allowLatePayment ? Number(values.lateDays) : undefined;

          payload.earlyPaymentDiscountEnabled = allowEarlyDiscount;
          const mapDiscountType = (t: string) => (t === 'fixo' ? 'FIXED' : 'PERCENTAGE');
          payload.earlyPaymentConditionType = allowEarlyDiscount
            ? (mapDiscountType(values.earlyDiscountType) as 'FIXED' | 'PERCENTAGE')
            : undefined;
          payload.earlyDiscountValue = allowEarlyDiscount ? Number(values.earlyDiscountValue.replace(/\D/g, '')) : undefined;
          payload.earlyPaymentDiscountDays = allowEarlyDiscount ? Number(values.earlyDiscountDays.replace(/\D/g, '')) : undefined;
        }

        if (mode === 'edit' && initialData?.id) {
          await service.update(Number(initialData.id), payload);
          toast.triggerToast('Beneficiário atualizado com sucesso.', { variant: 'success' });
        } else {
          await service.create(payload);
          toast.triggerToast('Beneficiário criado com sucesso.', { variant: 'success' });
        }

        resetForm();
        setSelectedType(EBeneficiaryType.ACCOUNT);
        onClose();
        onSubmitted?.();
      } catch (err) {
        const response = err as AxiosResponse;
        const backendMsg = response?.data?.detail || response?.data?.message;

        if (response?.status === 400 && typeof backendMsg === 'string') {
          toast.triggerToast(backendMsg, { variant: 'error' });
        } else {
          toast.triggerToast('Erro ao salvar o beneficiário. Verifique os dados.', { variant: 'error' });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    formik.validateForm();
  }, [selectedType, isEnvio, allowLatePayment, allowEarlyDiscount]);

  React.useEffect(() => {
    if (initialData && open) {
      if (initialData.pixKey) {
        setSelectedType(EBeneficiaryType.PIX_KEY);
      } else {
        setSelectedType(EBeneficiaryType.ACCOUNT);
      }
      if (type === 'boleto') {
        setAllowLatePayment(Boolean(initialData.allowLatePayment));
        setAllowEarlyDiscount(Boolean(initialData.earlyPaymentDiscountEnabled));
      }
    } else if (open && mode === 'create') {
      setSelectedType(EBeneficiaryType.ACCOUNT);
      if (type === 'boleto') {
        setAllowLatePayment(false);
        setAllowEarlyDiscount(false);
      }
    }
  }, [initialData, open, mode]);

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, submitForm, resetForm } = formik;

  const getErrorText = (name: string): string | undefined => {
    const isTouched = (touched as any)?.[name];
    const err = (errors as any)?.[name];
    return isTouched && typeof err === 'string' ? (err as string) : undefined;
  };
  const canSubmit = isValid && !isSubmitting;

  const handleOpenDelete = () => setDeleteDialogOpen(true);
  const handleCloseDelete = () => setDeleteDialogOpen(false);
  const handleConfirmDelete = async () => {
    try {
      if (!initialData?.id) return;
      await service.deactivate(Number(initialData.id));
      toast.triggerToast('Beneficiário desativado com sucesso.', { variant: 'success' });
      setDeleteDialogOpen(false);
      onClose();
      onSubmitted?.();
    } catch (err: any) {
      const status = err?.status;
      if (status === 404) {
        toast.triggerToast('Beneficiário não encontrado.', { variant: 'warning' });
      } else if (status === 409) {
        toast.triggerToast('Não é possível desativar: há vínculos relacionados.', { variant: 'error' });
      } else if (status === 403) {
        toast.triggerToast('Sem permissão para desativar este beneficiário.', { variant: 'error' });
      } else if (status === 401) {
        toast.triggerToast('Sessão expirada. Faça login novamente.', { variant: 'warning' });
      } else {
        toast.triggerToast('Erro ao desativar o beneficiário.', { variant: 'error' });
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '20px', pt: 1.5, pb: 1 }}>
            {type === 'boleto' ? 'Cadastrar contraparte para emissão de boletos' : 'Cadastrar contraparte para envio de recursos'}
          </Typography>
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: '45%', transform: 'translateY(-50%)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mb: 0, pb: 0 }}>
          <Box mb={2}>
            <BeneficiaryFields values={values} touched={touched} errors={errors} handleChange={handleChange} handleBlur={handleBlur} />
          </Box>

          {type === 'boleto' && (
            <Box mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography sx={{ fontSize: 16 }}>Endereço</Typography>
              </Box>
              <AddressFields
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
              />
              <Box display="flex" alignItems="center" gap={1} mb={1} mt={3}>
                <Typography sx={{ fontSize: 16 }}>Configurações</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={1}>
                Os dados abaixo serão automaticamente aplicados, mas poderão ser ajustados no momento da emissão do boleto
              </Typography>

              <Box bgcolor="#F7F9FC" pl={1.5} mb={2} borderRadius={2}>
                <FormControlLabel
                  control={<Switch checked={allowLatePayment} onChange={(_, c) => setAllowLatePayment(c)} />}
                  label="Permitir pagamento em atraso"
                  labelPlacement="start"
                  sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', ml: 0 }}
                />

                {allowLatePayment && (
                  <LatePaymentFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                )}
              </Box>

              <Box bgcolor="#F7F9FC" pl={1.5} borderRadius={2}>
                <FormControlLabel
                  control={<Switch checked={allowEarlyDiscount} onChange={(_, c) => setAllowEarlyDiscount(c)} />}
                  label="Desconto para pagamento antecipado"
                  labelPlacement="start"
                  sx={{ mb: 1, display: 'flex', width: '100%', justifyContent: 'space-between', ml: 0 }}
                />

                {allowEarlyDiscount && (
                  <EarlyDiscountFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                )}
              </Box>
            </Box>
          )}

          {type !== 'boleto' && (
            <Box mb={3}>
              <AccountOrPixFields
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                getErrorText={getErrorText}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetForm();
              }}
              sx={{ borderRadius: 6, px: 4, flex: 1 }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              disabled={!canSubmit}
              sx={{
                borderRadius: 6,
                px: 4,
                backgroundColor: canSubmit ? 'primary.main' : '#E0E0E0',
                flex: 1,
              }}
              onClick={async () => {
                await submitForm();
                resetForm();
              }}
            >
              {mode === 'edit' ? 'Salvar alterações' : 'Cadastrar'}
              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
            </Button>
          </Box>

          {mode === 'edit' && initialData?.id ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineOutlinedIcon />}
                sx={{ borderRadius: 6, borderColor: '#FF5252', color: '#FF5252', width: '20vw' }}
                onClick={handleOpenDelete}
              >
                Excluir Contraparte
              </Button>
            </Box>
          ) : null}
        </DialogActions>
      </Dialog>
      <DeleteBeneficiaryDialog open={deleteDialogOpen} onClose={handleCloseDelete} onConfirm={handleConfirmDelete} />
    </>
  );
};

export default CounterpartyModal;
